import { createSlice, createAsyncThunk, Dispatch } from '@reduxjs/toolkit';
import axios from 'axios';
import { RatesState, RatesUploadRequest, UploadRatesSuccessPayload, UploadRatesErrorPayload } from 'libs/redux-store/src/reprice/types';
import { RootStateOrAny } from 'react-redux';
import { apiClient } from '../api.client';

export interface RateItem {
    _id: string;
}

export const initialState: RatesState = {
    plans: [],
    categories: [],
    channels: [],
    effectiveDates: [],
    selectedPlan: null,
    selectedCategory: null,
    selectedChannel: null,
    selectedEffectiveDate: null,
    error: null,
    isRatesUploadSuccess: false,
    isRatesUploadFailure: false,
    ratesUploadError: null,
    success: null,
    deletePayload: {
        planCode: '',
        category: '',
        orgCategory: '',
        effectiveDate: ''
    }

};

interface FetchChannelsParams {
    benefitCode: string;
    category: string;
    channel?: string;
    selectedPlan: string;
    plans: [];
}
interface DeleteRatesPayload {
    planCode: string;
    category: string;
    orgCategory: string;
    effectiveDate: string;
}

export interface UploadRatesParamsData {
    payload: RatesUploadRequest[];
    formattedDate: string;
    isUploadOverwrite: boolean;
}

export const fetchPlans = createAsyncThunk('rates/fetchPlans', async () => {
    const response = await axios.get('/api/product/ratesDropdown');
    return response.data;
});

export const fetchCategories = createAsyncThunk(
    'rates/fetchCategories',
    async ({ benefitCode }: FetchChannelsParams) => {
        const response = await axios.get(`/api/product/ratesDropdown?benefit-code=${benefitCode}`);
        return response.data;
    }
);

export const fetchChannels = createAsyncThunk(
    'rates/fetchChannels',
    async ({ benefitCode, category }: FetchChannelsParams) => {
        const response = await axios.get(`/api/product/ratesDropdown?benefit-code=${benefitCode}&category=${category}`);
        return response.data;
    }
);

export const fetchEffectiveDates = createAsyncThunk(
    'rates/fetchEffectiveDates',
    async ({ benefitCode, category, channel }: FetchChannelsParams) => {
        const response = await axios.get(
            `/api/product/ratesDropdown?benefit-code=${benefitCode}&category=${category}&channel=${channel}`
        );
        return response.data;
    }
);

export const deleteRates = createAsyncThunk(
  'rates/deleteRates',
  async (deletePayload: DeleteRatesPayload, { rejectWithValue }) => {
    // Basic null/undefined check
    if (!deletePayload) {
      return rejectWithValue('Delete payload is required.');
    }

    // Example: Validate required fields inside the payload
    const requiredFields = [
        'planCode',
        'category',
        'orgCategory',
        'effectiveDate'
    ] // adjust based on your actual payload structure
    for (const field of requiredFields) {
      if (!deletePayload[field]) {
        return rejectWithValue(`Validation error: '${field}' is required.`);
      }
    }

    try {
      const response = await axios.post('/api/product/rates/deleteRates', null, {
        params: {
            planCode: deletePayload.planCode,
            category: deletePayload.category,
            orgCategory: deletePayload.orgCategory,
            effectiveDate: deletePayload.effectiveDate
        }
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error || 'Failed to delete rates');
    }
  }
);

export const uploadRates = createAsyncThunk
    <UploadRatesSuccessPayload,
        UploadRatesParamsData,
        {
            rejectValue: UploadRatesErrorPayload 
        }>
    (
        'rates/uploadRates',
        async (
            request,
            {
                getState,
                dispatch,
                rejectWithValue
            }: { getState: () => RootStateOrAny; dispatch: Dispatch; rejectWithValue: Function }
        ) => {
            try {
                const { payload, formattedDate, isUploadOverwrite } = request;
                const response = await apiClient(dispatch, getState).post(`api/product/rates/upload?effectiveDate=${formattedDate}&overwrite=${isUploadOverwrite}`, payload);
                return response.data;
            } catch (error: any) {
                return rejectWithValue(error?.response?.data || error);
            }
        }
    );

export const ratesSlice = createSlice({
    name: 'rates',
    initialState,
    reducers: {
        setSelectedPlan: (state, action) => {
            state.selectedPlan = action.payload;
        },

        setSelectedCategory: (state, action) => {
            state.selectedCategory = action.payload;
        },

        setSelectedChannel: (state, action) => {
            state.selectedChannel = action.payload;
        },

        setSelectedEffectiveDate: (state, action) => {
            state.selectedEffectiveDate = action.payload;
        },

        resetRatesState: () => {
            return initialState;
        },

        resetRatesUploadResponseState: (state) => {
            state.isRatesUploadFailure = false;
            state.isRatesUploadSuccess = false;
            state.ratesUploadError = null;
        }
    },

    extraReducers: builder => {
        builder
            .addCase(fetchPlans.fulfilled, (state, action) => {
                state.plans = action.payload;
            })
            .addCase(fetchPlans.rejected, (state, action) => {
                state.plans = [];
                state.error = action.error.message || 'Failed to fetch plans data';
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.categories = action.payload;
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.categories = [];
                state.error = action.error.message || 'Failed to fetch categories data';
            })
            .addCase(fetchChannels.fulfilled, (state, action) => {
                state.channels = action.payload;
            })
            .addCase(fetchChannels.rejected, (state, action) => {
                state.channels = [];
                state.error = action.error.message || 'Failed to fetch channels data';
            })
            .addCase(fetchEffectiveDates.fulfilled, (state, action) => {
                state.effectiveDates = action.payload;
            })
            .addCase(fetchEffectiveDates.rejected, (state, action) => {
                state.effectiveDates = [];
                state.error = action.error.message || 'Failed to fetch effective dates';
            })
            .addCase(deleteRates.fulfilled, (state, action) => {
                const { success, message } = action.payload;
                if (success) {
                    state.success = message;
                    state.error = null;
                } else {
                    state.success = null;
                    state.error = message || 'Delete failed';
                }
            })
            .addCase(deleteRates.rejected, (state, action) => {
                state.success = null;
                state.error = action.payload as string;
            })
            .addCase(uploadRates.fulfilled, state => {
                state.isRatesUploadSuccess = true;
            })
            .addCase(uploadRates.rejected, (state, action) => {
                state.isRatesUploadSuccess = false;
                state.isRatesUploadFailure = true;
                state.ratesUploadError = action.payload as UploadRatesErrorPayload;
            })
    }
});

export const { setSelectedPlan, setSelectedCategory, setSelectedChannel, setSelectedEffectiveDate, resetRatesState, resetRatesUploadResponseState} =
    ratesSlice.actions;

export default ratesSlice.reducer;

export const selectRatesUploadSuccess = (state: RootStateOrAny) => state.rates.isRatesUploadSuccess;
export const selectRatesUploadFailure = (state: RootStateOrAny) => state.rates.isRatesUploadFailure;
export const selectRatesUploadError = (state: RootStateOrAny) => state.rates.ratesUploadError;
